import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./GSTReport.css";

const API = (
  ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:8080"
    : process.env.REACT_APP_API_BASE_URL || "https://erp-api.sdcllp.co.in"
).replace(/\/$/, "");

const auth = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const first = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "") ?? "";

const dateKey = (v) => {
  if (!v) return "";
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
};

const fyStart = () => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  for (const value of [
    data?.data,
    data?.data?.documents,
    data?.data?.entries,
    data?.documents,
    data?.entries,
    data?.results,
  ]) {
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getId = (v) =>
  String(v?._id || v?.id || (typeof v === "string" ? v : ""));

const getInvoiceNo = (d) =>
  String(first(d?.number, d?.invoiceNumber, d?.invoiceNo)).trim();

const getParty = (d) =>
  first(
    d?.client?.companyName,
    d?.client?.name,
    d?.clientName,
    d?.vendor?.companyName,
    d?.vendor?.name,
    d?.vendorName,
    d?.partyName,
    d?.supplierName,
  );

const getGSTNo = (d) =>
  String(
    first(
      d?.gstNo,
      d?.gstNumber,
      d?.gstin,
      d?.clientGSTNo,
      d?.clientGSTIN,
      d?.client?.gstNo,
      d?.client?.gstNumber,
      d?.client?.gstin,
      d?.vendorGSTNo,
      d?.vendor?.gstNo,
      d?.vendor?.gstNumber,
      d?.vendor?.gstin,
    ),
  )
    .trim()
    .toUpperCase();

const findInvoiceFinanceEntry = (document, entries) => {
  const invoiceId = getId(document?._id);
  const invoiceNo = getInvoiceNo(document);
  const company = document?.company || "SDC";
  const income = entries.filter(
    (entry) => entry.type === "income" && (entry.company || "SDC") === company,
  );

  // Prefer the unique invoice document ID; fall back to invoice number only
  // for entries without a linked document ID.
  const byId =
    invoiceId &&
    income.find((entry) => getId(entry.invoiceDocumentId) === invoiceId);
  if (byId) return byId;
  return income.find(
    (entry) =>
      !getId(entry.invoiceDocumentId) &&
      invoiceNo &&
      String(entry.invoiceNo || "").trim() === invoiceNo,
  );
};

const getDiscount = (d) =>
  num(
    first(
      d?.discountTotal,
      d?.totalDiscountAmount,
      d?.discountAmount,
      d?.discount,
      0,
    ),
  );

const getPaid = (d) => {
  if (d?.paidAmount !== undefined && d?.paidAmount !== null)
    return num(d.paidAmount);

  if (Array.isArray(d?.payments))
    return d.payments.reduce((sum, p) => sum + num(p.amount), 0);

  return 0;
};

const getTax = (d) => {
  // InvoicePage stores tax per line item and uses taxableAmount/taxTotal.
  const lineItems = Array.isArray(d.items) ? d.items : [];
  const lineTax = lineItems.reduce((total, item) => {
    const base = num(item.quantity) * num(item.rate);
    const disc =
      item.discountType === "percentage"
        ? (base * num(item.discountValue)) / 100
        : item.discountType === "amount"
          ? num(item.discountValue)
          : 0;
    return total + (Math.max(0, base - disc) * num(item.gstRate)) / 100;
  }, 0);
  const gstAmount = num(
    first(
      d?.taxTotal,
      d?.gstAmount,
      d?.totalGST,
      d?.totalGst,
      d?.totalTax,
      d?.cgst !== undefined || d?.sgst !== undefined || d?.igst !== undefined
        ? num(d.cgst) + num(d.sgst) + num(d.igst)
        : undefined,
      lineItems.length ? lineTax : undefined,
      0,
    ),
  );

  const invoiceAmount = num(
    first(d?.grandTotal, d?.invoiceAmount, d?.totalAmount, 0),
  );

  const basicField = first(
    d?.taxableAmount,
    d?.basicAmount,
    d?.subTotal,
    d?.subtotal,
  );

  const basic = basicField !== "" ? num(basicField) : invoiceAmount - gstAmount;

  const gstPercent = num(
    first(
      d?.gstPercent,
      d?.gstRate,
      d?.gstPercentage,
      basic > 0 ? (gstAmount / basic) * 100 : 0,
    ),
  );

  return {
    basic,
    gstPercent,
    gstAmount,
    invoiceAmount,
  };
};

const getEntryTax = (e) => {
  const basic = num(e.amount);
  const gstPercent = num(e.gstPercent);
  const gstAmount = num(first(e.gstAmount, (basic * gstPercent) / 100));

  return {
    basic,
    gstPercent,
    gstAmount,
    invoiceAmount: basic + gstAmount,
  };
};

const sum = (rows, key) =>
  rows.reduce((total, row) => total + num(row[key]), 0);

const downloadCSV = (rows, columns, filename) => {
  const escape = (value) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const lines = [
    columns.map((c) => escape(c.label)).join(","),
    ...rows.map((r) =>
      columns
        .map((c) => escape(c.numeric ? num(r[c.key]).toFixed(2) : r[c.key]))
        .join(","),
    ),
  ];

  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const SALES_COLUMNS = [
  { key: "date", label: "Invoice Date" },
  { key: "party", label: "Client Name" },
  { key: "gstNo", label: "GST No." },
  { key: "invoiceNo", label: "Invoice No." },
  { key: "basic", label: "Basic Amount", numeric: true },
  { key: "gstPercent", label: "GST %", numeric: true },
  { key: "gstAmount", label: "GST Amount", numeric: true },
  { key: "invoiceAmount", label: "Invoice Amount", numeric: true },
  { key: "received", label: "Received Amount", numeric: true },
  { key: "discount", label: "Discount", numeric: true },
  { key: "outstanding", label: "Outstanding", numeric: true },
];

const PURCHASE_COLUMNS = [
  { key: "date", label: "Bill Date" },
  { key: "party", label: "Vendor Name" },
  { key: "gstNo", label: "GST No." },
  { key: "invoiceNo", label: "Bill No." },
  { key: "basic", label: "Basic Amount", numeric: true },
  { key: "gstPercent", label: "GST %", numeric: true },
  { key: "gstAmount", label: "GST Amount", numeric: true },
  { key: "invoiceAmount", label: "Bill Amount", numeric: true },
  { key: "received", label: "Paid Amount", numeric: true },
  { key: "discount", label: "Discount", numeric: true },
  { key: "outstanding", label: "Outstanding", numeric: true },
];

// The file endpoints below match the existing Finance Entry upload/download flow.
const absoluteProofUrl = (value) =>
  /^https?:\/\//i.test(value)
    ? value
    : `${API}${value.startsWith("/") ? "" : "/"}${value}`;
async function openGSTFile(url, name) {
  const popup = window.open("", "_blank");
  try {
    const response = await axios.get(url, { ...auth(), responseType: "blob" });
    const blobUrl = URL.createObjectURL(
      new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      }),
    );
    if (popup) popup.location.href = blobUrl;
    else {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || "document";
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (error) {
    if (popup) popup.close();
    alert(error.response?.data?.message || `Cannot open ${name}.`);
  }
}
function GSTDocuments({ row, related, onClose }) {
  const items = [];
  const add = (label, name, url) => {
    if (url) items.push({ label, name, url });
  };
  const collect = (entry, prefix) => {
    if (entry.invoiceFileKey)
      add(
        `${prefix} Invoice Attachment`,
        entry.invoiceFileName || "Invoice",
        `${API}/api/download?key=${encodeURIComponent(entry.invoiceFileKey)}`,
      );
    if (entry.otherDocFileKey)
      add(
        `${prefix} Income / Expense Proof`,
        entry.otherDocFileName || "Proof",
        `${API}/api/download?key=${encodeURIComponent(entry.otherDocFileKey)}`,
      );
    if (entry.proformaFileKey)
      add(
        `${prefix} Proforma`,
        entry.proformaFileName || "PI",
        `${API}/api/download?key=${encodeURIComponent(entry.proformaFileKey)}`,
      );
    (Array.isArray(entry.payments) ? entry.payments : []).forEach((p, i) => {
      if (p.proofUrl)
        add(
          `${prefix} Payment Proof ${i + 1}`,
          p.proofFileName || `Payment ${i + 1}`,
          absoluteProofUrl(String(p.proofUrl)),
        );
    });
  };
  const raw = row.raw || {};
  if (row.source === "Sales Invoice" && raw._id) {
    // Fetch the stored invoice PDF using the authenticated backend endpoint.
    add(
      "ERP Invoice",
      row.invoiceNo || "Invoice",
      `${API}/api/sales/documents/${encodeURIComponent(getId(raw._id))}/pdf`,
    );
  }
  collect(raw, row.source === "Sales Invoice" ? "Invoice" : "Expense");
  related
    .filter((e) => getId(e._id) !== getId(raw._id))
    .forEach((e) => collect(e, e.type === "expense" ? "Expense" : "Income"));
  const unique = items.filter(
    (item, i) => items.findIndex((other) => other.url === item.url) === i,
  );
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Invoice and proofs"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#0f172a99",
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          padding: 20,
          width: "min(650px,100%)",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <h3>Documents — {row.invoiceNo || row.party}</h3>
          <button type="button" onClick={onClose}>
            ✕ Close
          </button>
        </div>
        {unique.length ? (
          unique.map((item) => (
            <div
              key={item.url}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid #ddd",
              }}
            >
              <span>
                <b>{item.label}</b>
                <br />
                <small>{item.name}</small>
              </span>
              <button
                type="button"
                onClick={() => openGSTFile(item.url, item.name)}
              >
                View / Download
              </button>
            </div>
          ))
        ) : (
          <p>No document attached.</p>
        )}
      </div>
    </div>
  );
}

export default function GSTReport({ initialTab = "sales" }) {
  const tab = initialTab === "purchase" ? "purchase" : "sales";
  const [openDocs, setOpenDocs] = useState(null);
  const [company, setCompany] = useState("SDC");
  const [year, setYear] = useState(fyStart());
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(
    Math.floor(((new Date().getMonth() + 9) % 12) / 3) + 1,
  );
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const [docRes, entryRes] = await Promise.all([
        axios.get(`${API}/api/sales/documents`, auth()),
        axios.get(`${API}/api/finance-entries`, auth()),
      ]);

      setDocuments(listFrom(docRes.data));
      setEntries(listFrom(entryRes.data));
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Unable to load GST data",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const availableYears = useMemo(() => {
    const source =
      tab === "sales"
        ? documents.filter((d) => String(d.type).toLowerCase() === "invoice")
        : entries.filter((e) => e.type === "expense");
    const years = new Set(
      source
        .map((r) => {
          const date = dateKey(first(r.documentDate, r.invoiceDate, r.date));
          if (!date) return null;
          const y = Number(date.slice(0, 4));
          return Number(date.slice(5, 7)) >= 4 ? y : y - 1;
        })
        .filter((y) => y !== null),
    );
    return [...years].sort((a, b) => b - a);
  }, [tab, documents, entries]);

  useEffect(() => {
    if (availableYears.length && !availableYears.includes(Number(year)))
      setYear(availableYears[0]);
  }, [availableYears, year]);

  const salesRows = useMemo(() => {
    return documents
      .filter((d) => String(d.type || "").toLowerCase() === "invoice")
      .map((d) => {
        const tax = getTax(d);
        const received = getPaid(d);
        const linkedFinanceEntry = findInvoiceFinanceEntry(d, entries);
        const gstNo = getGSTNo(d) || getGSTNo(linkedFinanceEntry);

        return {
          id: getId(d._id),
          company: d.company || "SDC",
          date: dateKey(first(d.documentDate, d.invoiceDate, d.date)),
          party: getParty(d),
          gstNo,
          invoiceNo: getInvoiceNo(d),
          ...tax,
          received,
          discount: getDiscount(d),
          outstanding: Math.max(0, tax.invoiceAmount - received),
          source: "Sales Invoice",
          raw: d,
          incomplete:
            !gstNo ||
            !getInvoiceNo(d) ||
            !d.documentDate ||
            (tax.invoiceAmount > 0 && tax.basic === 0 && tax.gstAmount === 0),
        };
      });
  }, [documents, entries]);

  const purchaseRows = useMemo(() => {
    return entries
      .filter((e) => e.type === "expense")
      .filter(
        (e) =>
          e.billNo ||
          e.invoiceNo ||
          num(e.gstPercent) > 0 ||
          num(e.gstAmount) > 0,
      )
      .map((e) => {
        const tax = getEntryTax(e);

        return {
          id: getId(e._id),
          company: e.company || "SDC",
          date: dateKey(e.date),
          party: e.partyName || "",
          gstNo: getGSTNo(e),
          invoiceNo: first(e.billNo, e.invoiceNo),
          ...tax,
          received: num(e.paidAmount ?? 0),
          discount: num(e.discountAmount || e.discount),
          outstanding: Math.max(0, tax.invoiceAmount - num(e.paidAmount ?? 0)),
          source: "Finance Expense",
          raw: e,
          incomplete: (!e.billNo && !e.invoiceNo) || !getGSTNo(e),
        };
      });
  }, [entries]);

  const inPeriod = (date) => {
    if (!date) return false;

    if (period === "custom") {
      return (!from || date >= from) && (!to || date <= to);
    }

    const d = new Date(`${date}T12:00:00`);
    if (Number.isNaN(d.getTime())) return false;

    const fy = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;

    if (fy !== Number(year)) return false;

    if (period === "year") return true;

    const m = d.getMonth() + 1;

    if (period === "month") return m === Number(month);

    const q = Math.floor(((m + 8) % 12) / 3) + 1;
    return q === Number(quarter);
  };

  const rows = useMemo(() => {
    const source = tab === "sales" ? salesRows : purchaseRows;
    const q = search.toLowerCase().trim();

    return source
      .filter((r) => r.company === company)
      .filter((r) => inPeriod(r.date))
      .filter(
        (r) =>
          !q ||
          [r.party, r.gstNo, r.invoiceNo, r.date].some((v) =>
            String(v).toLowerCase().includes(q),
          ),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [
    tab,
    salesRows,
    purchaseRows,
    company,
    year,
    period,
    month,
    quarter,
    from,
    to,
    search,
  ]);

  const columns = tab === "sales" ? SALES_COLUMNS : PURCHASE_COLUMNS;
  const linkedEntries = (r) =>
    entries.filter((e) => {
      const d = r.raw;
      const docId = getId(d?._id);
      const linked = getId(e.invoiceDocumentId);
      return (
        (linked && linked === docId) ||
        (r.invoiceNo &&
          String(e.invoiceNo || "").trim() === r.invoiceNo &&
          (!e.company || e.company === r.company))
      );
    });

  const totals = {
    basic: sum(rows, "basic"),
    gst: sum(rows, "gstAmount"),
    invoice: sum(rows, "invoiceAmount"),
    received: sum(rows, "received"),
    discount: sum(rows, "discount"),
    outstanding: sum(rows, "outstanding"),
  };

  const exportReport = () => {
    downloadCSV(rows, columns, `GST_${tab}_${company}_${year}.csv`);
  };

  return (
    <div className="gst-page">
      <div className="gst-heading">
        <div>
          <h2>GST Reports</h2>
          <p>Sales and Purchase Tax Register</p>
        </div>

        <div className="gst-actions">
          <button onClick={load} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            className="gst-primary"
            onClick={exportReport}
            disabled={!rows.length}
          >
            Export Excel / CSV
          </button>
        </div>
      </div>

      <div className="gst-filters">
        <select value={company} onChange={(e) => setCompany(e.target.value)}>
          <option value="SDC">SDC</option>
        </select>

        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {availableYears.map((y) => (
            <option key={y} value={y}>
              FY {y}-{String(y + 1).slice(-2)}
            </option>
          ))}
        </select>

        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="month">Monthly</option>
          <option value="quarter">Quarterly</option>
          <option value="year">Financial Year</option>
          <option value="custom">Custom Dates</option>
        </select>

        {period === "month" && (
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
        )}

        {period === "quarter" && (
          <select
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
          >
            <option value={1}>Q1 Apr-Jun</option>
            <option value={2}>Q2 Jul-Sep</option>
            <option value={3}>Q3 Oct-Dec</option>
            <option value={4}>Q4 Jan-Mar</option>
          </select>
        )}

        {period === "custom" && (
          <>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </>
        )}

        <input
          placeholder="Search invoice, client, GST..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="gst-error">{error}</div>}

      <div className="gst-summary">
        {[
          ["Invoices / Bills", rows.length, false],
          ["Basic Amount", totals.basic, true],
          ["GST Amount", totals.gst, true],
          ["Invoice Amount", totals.invoice, true],
          [tab === "sales" ? "Received" : "Paid", totals.received, true],
          ["Discount", totals.discount, true],
          ["Outstanding", totals.outstanding, true],
        ].map(([label, value, currency]) => (
          <div className="gst-stat" key={label}>
            <span>{label}</span>
            <strong>{currency ? `₹ ${money(value)}` : value}</strong>
          </div>
        ))}
      </div>

      {tab === "purchase" && (
        <div className="gst-warning">
          Purchase register for reconciliation only. GST input tax credit
          eligibility and GSTR-3B filing values must be verified separately.
        </div>
      )}

      {rows.some((r) => r.incomplete) && (
        <div className="gst-warning">
          Some records have missing GST numbers, dates or tax details. Review
          highlighted rows before using this report.
        </div>
      )}

      <div className="gst-table-wrap">
        <table className="gst-table">
          <thead>
            <tr>
              <th>#</th>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              <th>Documents / Proof</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id || i}
                className={r.incomplete ? "gst-incomplete" : ""}
              >
                <td>{i + 1}</td>
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? "gst-number" : ""}>
                    {c.numeric ? money(r[c.key]) : r[c.key] || "—"}
                  </td>
                ))}
                <td>
                  <button type="button" onClick={() => setOpenDocs(r)}>
                    📎 View Documents
                  </button>
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td colSpan={columns.length + 2} className="gst-empty">
                  {loading ? "Loading report..." : "No records found"}
                </td>
              </tr>
            )}
          </tbody>

          {!!rows.length && (
            <tfoot>
              <tr>
                <td colSpan={5}>TOTAL</td>
                <td className="gst-number">{money(totals.basic)}</td>
                <td>—</td>
                <td className="gst-number">{money(totals.gst)}</td>
                <td className="gst-number">{money(totals.invoice)}</td>
                <td className="gst-number">{money(totals.received)}</td>
                <td className="gst-number">{money(totals.discount)}</td>
                <td className="gst-number">{money(totals.outstanding)}</td>
                <td>—</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {openDocs && (
        <GSTDocuments
          row={openDocs}
          related={linkedEntries(openDocs)}
          onClose={() => setOpenDocs(null)}
        />
      )}
      <div className="gst-footer">
        Showing {rows.length} records • {company} • FY {year}-
        {String(year + 1).slice(-2)}
      </div>
    </div>
  );
}
